import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import CalendarCta from '../components/common/CalendarCta';
import Icon from '../components/common/Icon';
import HeroNetwork from '../components/home/HeroNetwork';
import TiltCard from '../components/home/TiltCard';
import CountUp from '../components/home/CountUp';
import SolutionsCarousel from '../components/home/SolutionsCarousel';
import ProcessSteps from '../components/home/ProcessSteps';
import BeforeAfterSlider from '../components/home/BeforeAfterSlider';
import SavingsCalculator from '../components/home/SavingsCalculator';
import HomeFaq from '../components/home/HomeFaq';
import useScrollReveal from '../hooks/useScrollReveal';
import useButtonEffects from '../hooks/useButtonEffects';
import usePauseOffscreen from '../hooks/usePauseOffscreen';
import { tools } from '../data/tools';
import { brand, caseStudies, faqItems, homeContent, pageMeta } from '../data/siteContent';
import { GA_EVENTS, trackEvent } from '../utils/analytics';
import { hasFinePointer, shouldAnimate } from '../utils/motion';
import {
  buildBreadcrumbJsonLd,
  buildFaqJsonLd,
  buildOrganizationJsonLd,
  buildPageMeta,
  buildWebsiteJsonLd,
} from '../utils/seo';
import './HomePage.css';

const SAVINGS_TOOL_SLUG = 'ahorro-automatizacion';

const TOOL_CATEGORY_ICONS = {
  productividad: 'doc',
  soldadura: 'flame',
  'mecánica': 'wrench',
  'construcción': 'building',
  'eléctrico': 'zap',
  negocio: 'chart',
};

const formatUpdatedAt = (date) =>
  new Date(`${date}T12:00:00`).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });

function HeroTitle({ title, highlight }) {
  const index = title.indexOf(highlight);
  if (index === -1) return <h1>{title}</h1>;
  return (
    <h1>
      {title.slice(0, index)}
      <em>{highlight}</em>
      {title.slice(index + highlight.length)}
    </h1>
  );
}

function TrustGroup({ hidden = false }) {
  return (
    <div className="home-marquee-group" aria-hidden={hidden ? 'true' : undefined}>
      {homeContent.trustItems.map((item) => (
        <span key={item.label}>
          <Icon name={item.icon} size={18} strokeWidth={1.9} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

function ReportFlow() {
  return (
    <div className="home-flow" aria-hidden="true" data-loop>
      <div className="home-flow-files">
        <span>XLS</span>
        <span>CSV</span>
        <span>PDF</span>
        <span>XLS</span>
      </div>
      <div className="home-flow-pipe" />
      <div className="home-flow-dash">
        <div className="home-flow-kpi">
          <b>Reporte listo</b>
          <small>auditoría</small>
        </div>
        <div className="home-flow-bars">
          <i style={{ '--h': '45%' }} />
          <i style={{ '--h': '72%' }} />
          <i style={{ '--h': '58%' }} />
          <i style={{ '--h': '92%' }} />
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const rootRef = useRef(null);
  useScrollReveal(rootRef);
  useButtonEffects(rootRef);
  usePauseOffscreen(rootRef);

  const meta = buildPageMeta(pageMeta.home);
  const { hero, cases } = homeContent;
  const featuredTools = tools.filter((tool) => tool.featured && tool.slug !== SAVINGS_TOOL_SLUG).slice(0, 3);

  const handleSpotlight = (event) => {
    if (!shouldAnimate() || !hasFinePointer()) return;
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty('--spot-x', `${event.clientX - rect.left}px`);
    event.currentTarget.style.setProperty('--spot-y', `${event.clientY - rect.top}px`);
  };

  return (
    <div className="home" ref={rootRef}>
      <SEOHead
        title={meta.title}
        description={meta.description}
        canonical={meta.canonical}
        ogImage={meta.ogImage}
        jsonLd={[
          buildOrganizationJsonLd(),
          buildWebsiteJsonLd(),
          buildBreadcrumbJsonLd([{ name: 'Inicio', path: '/' }]),
          buildFaqJsonLd(faqItems),
        ]}
      />

      <section className="home-hero" data-loop>
        <HeroNetwork />
        <div className="container home-hero-layout">
          <div className="home-hero-copy">
            <span className="eyebrow-text">{hero.eyebrow}</span>
            <HeroTitle title={hero.title} highlight={hero.titleHighlight} />
            <p className="home-lead">{hero.subtitle}</p>
            <div className="home-hero-actions">
              <Link
                to={hero.primaryCta.href}
                className="btn-cta"
                data-magnetic
                onClick={() => trackEvent(GA_EVENTS.CLICK_CTA_SERVICIO, { source: 'home_hero' })}
              >
                {hero.primaryCta.label}
              </Link>
              <Link to={hero.secondaryCta.href} className="home-link-arrow">{hero.secondaryCta.label}</Link>
            </div>
            <ul className="home-assurances">
              {hero.assurances.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>

          <div className="home-hero-visual">
            <TiltCard as="aside" className="home-bill" aria-label="Resultado de un caso real de optimización AWS">
              <div className="home-bill-head">
                <div>
                  <small>{hero.caseCard.label}</small>
                  <h2>{hero.caseCard.title}</h2>
                </div>
                <span className="home-bill-saving">−<CountUp value={hero.caseCard.savingPercent} delay={900} />%</span>
              </div>
              <div className="home-bill-row">
                <span>Antes</span>
                <div className="home-bill-bar home-bill-bar--before" />
                <b>~$<CountUp value={hero.caseCard.before} delay={400} /></b>
              </div>
              <div className="home-bill-row">
                <span>Después</span>
                <div><div className="home-bill-bar home-bill-bar--after" /></div>
                <b>&lt;$<CountUp value={hero.caseCard.after} delay={900} /></b>
              </div>
              <div className="home-bill-foot">
                <span>{hero.caseCard.note}</span>
                <Link to={hero.caseCard.href} className="home-link-arrow">Ver el caso</Link>
              </div>
              <div className="home-bill-report">
                <span className="home-bill-report-icon"><Icon name="sheet" size={20} strokeWidth={1.9} /></span>
                <p><strong>{hero.caseCard.reportTitle}</strong> {hero.caseCard.reportText}</p>
              </div>
            </TiltCard>
          </div>
        </div>
      </section>

      <div className="home-trust" data-loop>
        <div className="home-marquee">
          <TrustGroup />
          <TrustGroup hidden />
        </div>
      </div>

      <section className="section-padding">
        <div className="container">
          <div className="home-heading" data-reveal>
            <span className="eyebrow-text">Problemas que resolvemos</span>
            <h2>¿Te suena alguno de estos?</h2>
            <p>Cada síntoma tiene una solución concreta. Empieza por el que más le cuesta a tu operación.</p>
          </div>
          <div className="home-symptoms" data-reveal>
            <div className="home-symptom home-symptom--head" aria-hidden="true">
              <span>Lo que pasa hoy</span>
              <span>Lo que hacemos</span>
              <span />
            </div>
            {homeContent.problems.map((item) => (
              <Link key={item.symptom} to={item.href} className="home-symptom">
                <span className="home-symptom-text">
                  <span className="home-symptom-icon"><Icon name={item.icon} size={18} strokeWidth={1.9} /></span>
                  <span>{item.symptom}</span>
                </span>
                <p>{item.fix}</p>
                <span className="home-link-arrow">{item.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding home-band">
        <div className="container">
          <div className="home-heading" data-reveal>
            <span className="eyebrow-text">Soluciones principales</span>
            <h2>Una solución proporcional a tu problema</h2>
          </div>
          <SolutionsCarousel solutions={homeContent.solutions} terminal={homeContent.auditTerminal} />
        </div>
      </section>

      <section className="section-padding">
        <div className="container">
          <div className="home-heading" data-reveal>
            <span className="eyebrow-text">Cómo trabajamos</span>
            <h2>De la primera llamada a un resultado medible</h2>
          </div>
          <ProcessSteps steps={homeContent.process} />
        </div>
      </section>

      <section className="section-padding home-band">
        <div className="container">
          <div className="home-heading" data-reveal>
            <span className="eyebrow-text">Casos de éxito</span>
            <h2>Resultados que puedes revisar</h2>
          </div>
          <div className="home-cases">
            <article className="home-case" data-reveal>
              <span className="eyebrow-text">{cases.aws.eyebrow}</span>
              <div className="home-metric">
                <span className="home-metric-from">$4,000</span>
                <span className="home-metric-value">&lt;$<CountUp value={2000} /> MXN</span>
                <span>/mes</span>
              </div>
              <h3>{caseStudies.aws.title}</h3>
              <BeforeAfterSlider
                before={cases.aws.comparison.before}
                after={cases.aws.comparison.after}
                rows={cases.aws.comparison.rows}
              />
              <small className="home-note">{cases.aws.comparison.note}</small>
              <Link to={cases.aws.href} className="home-link-arrow">Ver caso completo</Link>
            </article>
            <article className="home-case" data-reveal style={{ '--reveal-delay': '0.12s' }}>
              <span className="eyebrow-text">{cases.reports.eyebrow}</span>
              <div className="home-metric">
                <span className="home-metric-value">{cases.reports.metric}</span>
                <span>{cases.reports.metricUnit}</span>
              </div>
              <h3>{caseStudies.reports.title}</h3>
              <ReportFlow />
              <p>{caseStudies.reports.description}</p>
              <small className="home-note">Actualizado {formatUpdatedAt(caseStudies.reports.updatedAt)}</small>
              <Link to={cases.reports.href} className="home-link-arrow">Ver caso completo</Link>
            </article>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container">
          <div className="home-heading" data-reveal>
            <span className="eyebrow-text">Herramientas gratuitas</span>
            <h2>{homeContent.leadMagnets.title}</h2>
            <p>{homeContent.leadMagnets.description}</p>
          </div>
          <div className="home-tools">
            <div data-reveal>
              <SavingsCalculator
                toolHref={`/herramientas/${SAVINGS_TOOL_SLUG}`}
                ctaHref="/contacto?intent=automatizacion"
              />
            </div>
            <div className="home-tool-list">
              {featuredTools.map((tool, index) => (
                <Link
                  key={tool.slug}
                  to={`/herramientas/${tool.slug}`}
                  className="home-tool"
                  data-reveal
                  style={{ '--reveal-delay': `${index * 0.08}s` }}
                  onClick={() => trackEvent(GA_EVENTS.CLICK_HERRAMIENTA, { tool: tool.slug, source: 'home' })}
                >
                  <span className="home-tool-icon"><Icon name={TOOL_CATEGORY_ICONS[tool.category] || 'calculator'} size={20} /></span>
                  <span className="home-tool-category">{tool.category}</span>
                  <h3>{tool.name}</h3>
                  <span className="home-tool-go" aria-hidden="true">→</span>
                </Link>
              ))}
              <Link to="/herramientas" className="home-link-arrow">Ver todas las herramientas</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding home-band">
        <div className="container home-faq-layout">
          <div className="home-heading" data-reveal>
            <span className="eyebrow-text">Preguntas frecuentes</span>
            <h2>Antes de agendar</h2>
            <p>¿Tienes otra duda? Escríbenos por WhatsApp al {brand.phone}.</p>
          </div>
          <div data-reveal>
            <HomeFaq items={faqItems} />
          </div>
        </div>
      </section>

      <section className="home-final" onPointerMove={handleSpotlight}>
        <div className="container home-final-inner">
          <div data-reveal>
            <h2>{homeContent.finalCta.title}</h2>
            <p>{homeContent.finalCta.description}</p>
          </div>
          <div className="home-final-actions" data-reveal style={{ '--reveal-delay': '0.1s' }}>
            <Link
              to={homeContent.finalCta.primaryCta.href}
              className="btn-cta"
              data-magnetic
              onClick={() => trackEvent(GA_EVENTS.CLICK_CTA_SERVICIO, { source: 'home_final' })}
            >
              {homeContent.finalCta.primaryCta.label}
            </Link>
            <div className="home-final-alt">
              <a
                href={brand.whatsappHref}
                target="_blank"
                rel="noreferrer"
                className="home-btn-ghost"
                onClick={() => trackEvent(GA_EVENTS.CLICK_WHATSAPP, { source: 'home_final' })}
              >
                <Icon name="message" size={18} strokeWidth={1.9} />
                WhatsApp
              </a>
              <CalendarCta source="home_final" className="home-btn-ghost" label="Reservar llamada">
                <Icon name="calendar" size={18} strokeWidth={1.9} />
              </CalendarCta>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
